import { z } from 'zod';
import { BusinessDateSchema, IdSchema } from './encoding.js';
/**
 * HR Employee Master contracts (ERD §7.3; FRD-HRP-005 to HRP-013). Only the Release 1 master:
 * no lifecycle state, assignment history, documents, leave, requests or payroll data.
 *
 * The Employee ID (`code`) is generated by the system (FRD-CORE-035): it is in every response and
 * in no request. `status` is the Release 1 usability state, active or inactive (FRD-HRP-010).
 */
const MasterStatusSchema = z.enum(['active', 'inactive']);
const NameSchema = z.string().trim().min(1).max(200);
const EmailSchema = z.email().max(254);
const PhoneSchema = z.string().trim().min(1).max(50);
const ContactNameSchema = z.string().trim().min(1).max(200);
const TitleSchema = z.string().trim().min(1).max(100);
/** No identifier format is defined by any source, so only the length is bounded. */
const IdentifierSchema = z.string().trim().min(1).max(50);
const CountrySchema = z.string().trim().min(1).max(100);
const AddressSchema = z.string().trim().min(1).max(500);
const OrganisationNameSchema = z.string().trim().min(1).max(150);
/**
 * One education or certificate entry on the employee profile (FRD-HRP-078, CLR-40). Descriptive
 * only: a certificate's scan and its expiry alerts are employee documents (FRD-HRP-017).
 */
export const EmployeeQualificationSchema = z.strictObject({
    kind: z.enum(['education', 'certificate']),
    /** The qualification or certificate, e.g. a degree or a professional certificate. */
    title: z.string().trim().min(1).max(200),
    /** The institution or issuing body. */
    institution: z.string().trim().min(1).max(200).nullable(),
    /** The year completed or issued. */
    year: z.number().int().min(1900).max(2100).nullable(),
    /** For a certificate that expires. */
    expiryDate: BusinessDateSchema.nullable(),
});
const QualificationsSchema = z.array(EmployeeQualificationSchema).max(50);
/** A value of the configurable list `hr.employee_grade`, for the employee form. */
export const EmployeeGradeSchema = z.object({
    valueCode: z.string(),
    label: z.string(),
    active: z.boolean(),
});
export const EmployeeSchema = z.object({
    id: IdSchema,
    /** The generated Employee ID. */
    code: z.string(),
    name: z.string(),
    joiningDate: z.string(),
    contactEmail: z.string().nullable(),
    contactPhone: z.string().nullable(),
    emergencyContactName: z.string().nullable(),
    emergencyContactPhone: z.string().nullable(),
    departmentId: IdSchema,
    costCenterId: IdSchema,
    locationId: IdSchema,
    positionId: IdSchema.nullable(),
    reportingManagerId: IdSchema.nullable(),
    /**
     * Identity / compliance details (ERD §7.3 "identity details (S)", confirmed by management
     * 24 September 2026). The identifiers are designated sensitive: masked (`•••• 1234`, or `••••`
     * when empty) unless the caller holds the `identity` unmasked grant.
     */
    emiratesIdNumber: z.string().nullable(),
    emiratesIdExpiryDate: z.string().nullable(),
    passportNumber: z.string().nullable(),
    passportExpiryDate: z.string().nullable(),
    residencePermitNumber: z.string().nullable(),
    residencePermitExpiryDate: z.string().nullable(),
    workPermitNumber: z.string().nullable(),
    workPermitExpiryDate: z.string().nullable(),
    /**
     * Profile details (FRD-HRP-072 to HRP-078, CLR-40). Date of birth, the bank details and the
     * insurance identifiers are designated sensitive and arrive masked without the unmasked grant.
     * The API always sends them; a reader defaults them so a page served during a deployment still
     * reads an employee from the previous API version.
     */
    birthDate: z.string().nullable().default(null),
    nationality: z.string().nullable().default(null),
    /** A value of the configurable list `hr.employee_grade`. */
    gradeCode: z.string().nullable().default(null),
    /**
     * CLR-47: optional, set by HR. `••••` when an administrator has designated it sensitive and the
     * caller may not see it (CLR-49); a request never carries that form.
     */
    gender: z.enum(['male', 'female', '••••']).nullable().default(null),
    /** A value of the list `hr.education_level` (CLR-47). */
    educationLevelCode: z.string().nullable().default(null),
    /** Recorded when the employee leaves (CLR-47; an inactive employee only). */
    exitDate: z.string().nullable().default(null),
    exitReason: z.string().nullable().default(null),
    homeAddress: z.string().nullable().default(null),
    homeCountry: z.string().nullable().default(null),
    homePhone: z.string().nullable().default(null),
    bankName: z.string().nullable().default(null),
    bankAccountHolder: z.string().nullable().default(null),
    bankIban: z.string().nullable().default(null),
    insuranceProvider: z.string().nullable().default(null),
    insurancePolicyNumber: z.string().nullable().default(null),
    insuranceCardNumber: z.string().nullable().default(null),
    insuranceExpiryDate: z.string().nullable().default(null),
    qualifications: z.array(EmployeeQualificationSchema).default([]),
    status: MasterStatusSchema,
    /** For `If-Match` on a change (SDD §7.3). */
    version: z.number().int(),
});
/**
 * `GET /api/v1/hr/employees/{id}/picture-type` (FRD-HRP-071): the document type that holds this
 * employee's picture, or `null` while `hr.employee_picture_document_type` is unset. The picture
 * itself is the employee's current available document of that type.
 */
export const EmployeePictureTypeSchema = z.object({
    /** The employee's id. */
    id: IdSchema,
    documentTypeCode: z.string().nullable(),
});
/**
 * The employee's business-card link (FRD-HRP-079, CLR-65): the business-card code opens
 * `/card/<token>`, which anyone holding it can read signed out. `id` is the employee's.
 */
export const EmployeeBusinessCardLinkSchema = z.object({
    id: IdSchema,
    token: z.string().regex(/^[0-9a-f]{64}$/),
});
/**
 * The public business card behind a link (CLR-65): the work contact only, masked as for a viewer
 * with no grant, so a designated field arrives masked. `id` is the card's token; `photo` says
 * whether `…/photo` serves the employee's picture.
 */
export const PublicBusinessCardSchema = z.object({
    id: z.string(),
    name: z.string(),
    position: z.string().nullable(),
    company: z.string().nullable(),
    contactEmail: z.string().nullable(),
    contactPhone: z.string().nullable(),
    photo: z.boolean(),
});
/** The most rows an employee's current work holds (management request, 23 October 2026). */
export const EMPLOYEE_WORK_MAX_ROWS = 20;
export const EMPLOYEE_WORK_PART_MAX_LENGTH = 200;
/**
 * One row of what an employee is working on: a project, a product or service, and the part of it
 * they handle. Every field is optional, but a row is never empty.
 */
export const EmployeeWorkRowSchema = z.object({
    projectId: IdSchema.nullable(),
    /** "code — name", or null when the row names no project. */
    projectLabel: z.string().nullable(),
    productServiceId: IdSchema.nullable(),
    productServiceLabel: z.string().nullable(),
    part: z.string().nullable(),
});
/** `GET /api/v1/hr/employees/{id}/work`: the rows in order, and the employee's version. */
export const EmployeeWorkSchema = z.object({
    employeeId: IdSchema,
    rows: z.array(EmployeeWorkRowSchema),
    /** The employee's version, for `If-Match` on a replacement. */
    version: z.number().int(),
});
const EmployeeWorkRowInputSchema = z
    .strictObject({
    projectId: IdSchema.nullable(),
    productServiceId: IdSchema.nullable(),
    part: z.string().trim().min(1).max(EMPLOYEE_WORK_PART_MAX_LENGTH).nullable(),
})
    .refine((row) => row.projectId !== null || row.productServiceId !== null || row.part !== null, 'A row names a project, a product or service, or the part worked on');
/** `PUT /api/v1/hr/employees/{id}/work` with `If-Match`: replaces the list as a whole. */
export const ReplaceEmployeeWorkRequestSchema = z.strictObject({
    rows: z.array(EmployeeWorkRowInputSchema).max(EMPLOYEE_WORK_MAX_ROWS),
});
/**
 * `GET /api/v1/hr/work?productServiceId=…` (or `projectId=…`): the employees working on it whom
 * the caller may see, with the part each one handles.
 */
export const WorkerSchema = z.object({
    employeeId: IdSchema,
    employeeCode: z.string(),
    employeeName: z.string(),
    projectLabel: z.string().nullable(),
    productServiceLabel: z.string().nullable(),
    part: z.string().nullable(),
});
export const WorkersQuerySchema = z
    .strictObject({ projectId: IdSchema.optional(), productServiceId: IdSchema.optional() })
    .refine((q) => (q.projectId === undefined) !== (q.productServiceId === undefined), 'Name exactly one of projectId or productServiceId');
export const CreateEmployeeRequestSchema = z.strictObject({
    name: NameSchema,
    joiningDate: BusinessDateSchema,
    contactEmail: EmailSchema.optional(),
    contactPhone: PhoneSchema.optional(),
    emergencyContactName: ContactNameSchema.optional(),
    emergencyContactPhone: PhoneSchema.optional(),
    departmentId: IdSchema,
    costCenterId: IdSchema,
    locationId: IdSchema,
    positionId: IdSchema.optional(),
    reportingManagerId: IdSchema.optional(),
    emiratesIdNumber: IdentifierSchema.optional(),
    emiratesIdExpiryDate: BusinessDateSchema.optional(),
    passportNumber: IdentifierSchema.optional(),
    passportExpiryDate: BusinessDateSchema.optional(),
    residencePermitNumber: IdentifierSchema.optional(),
    residencePermitExpiryDate: BusinessDateSchema.optional(),
    workPermitNumber: IdentifierSchema.optional(),
    workPermitExpiryDate: BusinessDateSchema.optional(),
    birthDate: BusinessDateSchema.optional(),
    nationality: CountrySchema.optional(),
    gradeCode: IdentifierSchema.optional(),
    gender: z.enum(['male', 'female']).optional(),
    educationLevelCode: IdentifierSchema.optional(),
    homeAddress: AddressSchema.optional(),
    homeCountry: CountrySchema.optional(),
    homePhone: PhoneSchema.optional(),
    bankName: OrganisationNameSchema.optional(),
    bankAccountHolder: ContactNameSchema.optional(),
    bankIban: IdentifierSchema.optional(),
    insuranceProvider: OrganisationNameSchema.optional(),
    insurancePolicyNumber: IdentifierSchema.optional(),
    insuranceCardNumber: IdentifierSchema.optional(),
    insuranceExpiryDate: BusinessDateSchema.optional(),
    qualifications: QualificationsSchema.optional(),
});
/** A partial change. An optional field is cleared with `null`; the Employee ID never changes. */
export const UpdateEmployeeRequestSchema = z.strictObject({
    name: NameSchema.optional(),
    joiningDate: BusinessDateSchema.optional(),
    contactEmail: EmailSchema.nullable().optional(),
    contactPhone: PhoneSchema.nullable().optional(),
    emergencyContactName: ContactNameSchema.nullable().optional(),
    emergencyContactPhone: PhoneSchema.nullable().optional(),
    departmentId: IdSchema.optional(),
    costCenterId: IdSchema.optional(),
    locationId: IdSchema.optional(),
    positionId: IdSchema.nullable().optional(),
    reportingManagerId: IdSchema.nullable().optional(),
    emiratesIdNumber: IdentifierSchema.nullable().optional(),
    emiratesIdExpiryDate: BusinessDateSchema.nullable().optional(),
    passportNumber: IdentifierSchema.nullable().optional(),
    passportExpiryDate: BusinessDateSchema.nullable().optional(),
    residencePermitNumber: IdentifierSchema.nullable().optional(),
    residencePermitExpiryDate: BusinessDateSchema.nullable().optional(),
    workPermitNumber: IdentifierSchema.nullable().optional(),
    workPermitExpiryDate: BusinessDateSchema.nullable().optional(),
    birthDate: BusinessDateSchema.nullable().optional(),
    nationality: CountrySchema.nullable().optional(),
    gradeCode: IdentifierSchema.nullable().optional(),
    gender: z.enum(['male', 'female']).nullable().optional(),
    educationLevelCode: IdentifierSchema.nullable().optional(),
    /** With `status: 'inactive'`: when and why the employee left (CLR-47). */
    exitDate: BusinessDateSchema.nullable().optional(),
    exitReason: z.string().trim().min(1).max(500).nullable().optional(),
    homeAddress: AddressSchema.nullable().optional(),
    homeCountry: CountrySchema.nullable().optional(),
    homePhone: PhoneSchema.nullable().optional(),
    bankName: OrganisationNameSchema.nullable().optional(),
    bankAccountHolder: ContactNameSchema.nullable().optional(),
    bankIban: IdentifierSchema.nullable().optional(),
    insuranceProvider: OrganisationNameSchema.nullable().optional(),
    insurancePolicyNumber: IdentifierSchema.nullable().optional(),
    insuranceCardNumber: IdentifierSchema.nullable().optional(),
    insuranceExpiryDate: BusinessDateSchema.nullable().optional(),
    qualifications: QualificationsSchema.optional(),
    status: MasterStatusSchema.optional(),
});
/** Position: HR reference data, a title (FRD-HRP-009; ERD §7.3). */
export const PositionSchema = z.object({
    id: IdSchema,
    title: z.string(),
    status: MasterStatusSchema,
    version: z.number().int(),
});
export const CreatePositionRequestSchema = z.strictObject({
    title: TitleSchema,
});
export const UpdatePositionRequestSchema = z.strictObject({
    title: TitleSchema.optional(),
    status: MasterStatusSchema.optional(),
});
//# sourceMappingURL=hr.js.map